/**
 * A BROAD DIG, SHOWN BEFORE IT SPENDS.
 *
 * Clicking "Dig into <item>", a table's Dig or the lens's opens this at once:
 * first the brief being written — the room reading what it knows and deciding
 * each seat's job — then every prompt that will actually go out: the purpose
 * and questions every seat reads, and each seat's own job. All of it editable.
 * Dig starts exactly that; Cancel lets it go and stages nothing.
 *
 * When no brief could be written, the dig the button always sent is shown the
 * same way — its one directive, editable — so the choice is still the reader's.
 */
import { useEffect, type ReactNode } from 'react';
import { whoLabel, type StagedDig } from './reportsView';
import { DEPTH_WORDS, aimLabel, depthHint, scopeByKey, type Aim, type AimScope, type Depth } from './researchAim';

/** One choice in a row of them. A radio in all but looks: the group is small,
 *  the options are short, and a person should be able to see every answer at
 *  once rather than open something to find out what the choices were. */
function Choice({
  on,
  onPick,
  label,
  hint,
  testId,
  badge,
}: {
  on: boolean;
  onPick: () => void;
  label: string;
  hint: ReactNode;
  testId: string;
  /** "Recommended", beside the label. */
  badge?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onPick}
      data-testid={testId}
      className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-left transition ${
        on
          ? 'border-teal-500 bg-teal-50 dark:border-teal-400 dark:bg-teal-900/20'
          : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-900 dark:text-gray-100">
        <span className="truncate">{label}</span>
        {badge && (
          <span className="shrink-0 rounded bg-teal-600 px-1.5 py-px text-[10px] font-medium text-white" data-testid={`${testId}-badge`}>
            {badge}
          </span>
        )}
      </div>
      {hint && (
        <div className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">{hint}</div>
      )}
    </button>
  );
}

export default function DigModal({
  title,
  dig,
  onChange,
  onDig,
  onCancel,
  digging,
  budgetLabel,
  budgetPicker,
  aim,
  scopes,
  onAim,
  onResearch,
  recommended,
}: {
  /** The room's own advice on the first question, with its reason. */
  recommended?: { depth: Depth; reason: string } | null;
  /** What is being dug into: "the competitor table", "EliseAI Platform". */
  title: string;
  /** Null while the brief is still being written. */
  dig: StagedDig | null;
  onChange: (next: StagedDig) => void;
  onDig: (dig: StagedDig) => void;
  onCancel: () => void;
  /** A dig is already going: the button says so instead of starting a second. */
  digging: boolean;
  /** The spend, as words, when it cannot be changed here. */
  budgetLabel?: string;
  /** THE SPEND, CHOSEN HERE: the page's `BudgetPicker`, so the size of a dig is
   *  decided in the window that starts it. Shown in place of `budgetLabel`. */
  budgetPicker?: ReactNode;
  /** WHAT IS BEING ASKED, and where. Left out for the dig buttons that already
   *  know both — a row's own Dig is not a question about scope. */
  aim?: Aim;
  scopes?: AimScope[];
  onAim?: (next: Aim) => void;
  /** THE RESEARCH WINDOW ONLY ASKS. Set, the window shows its two questions
   *  and nothing else — no brief written while it is open, no prompts to read
   *  — and Research hands the choice back; the dig is worked out after the
   *  window closes, with the room's status bar saying so. */
  onResearch?: () => void;
}) {
  const asking = Boolean(aim && scopes?.length && onAim);
  const chosen = aim && scopes ? scopeByKey(scopes, aim.scope) : null;
  // The room's advice is about the whole room, so it shows only when aimed there.
  const roomAdvice = chosen?.kind === 'room' ? recommended : null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const ready = dig !== null;
  // AN AIMED DIG NEEDS NO WORDS. A run is an intent, a budget and a scope; the
  // directive is optional steering. Requiring one disabled Research for "fill
  // the gaps in the whole room", which is the plainest ask there is — the
  // window had both answers and the button still would not press.
  const asksOnly = asking && Boolean(onResearch);
  const canDig = asksOnly
    ? !digging
    : ready && !digging && (asking || Boolean(dig.directive.trim()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={asking ? 'Research' : `Dig into ${title}`}
        className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-xl bg-white dark:bg-gray-800 shadow-xl"
        onClick={e => e.stopPropagation()}
        data-testid="dig-modal"
      >
        <header className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-5 py-3">
          {/* A row's own Dig button already knows what it is digging into and
              asks nothing, so it keeps saying so. */}
          <div className="text-xs uppercase tracking-wide text-gray-400">
            {asking ? 'Research' : 'Dig into'}
          </div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {asking && aim && chosen ? aimLabel(aim.depth, chosen) : title}
          </h2>
        </header>

        {/* THE TWO QUESTIONS, ASKED ONCE, HERE.
            Not two buttons outside: "wider" and "deeper" are what to do, and
            "this table" or "the room" is where — pairing them into two buttons
            answered one of them on the reader's behalf, and the one screen they
            were watching was the one place nothing landed. */}
        {asking && aim && scopes && onAim && (
          <div className="shrink-0 space-y-3 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">What should it do</div>
              <div className="flex gap-2" role="radiogroup" aria-label="What should it do">
                {(['DEEPER', 'WIDER'] as Depth[]).map(d => (
                  <Choice
                    key={d}
                    on={aim.depth === d}
                    onPick={() => onAim({ ...aim, depth: d })}
                    label={DEPTH_WORDS[d].label}
                    hint={depthHint(d, scopeByKey(scopes, aim.scope))}
                    testId={`aim-depth-${d.toLowerCase()}`}
                    badge={roomAdvice?.depth === d ? 'Recommended' : undefined}
                  />
                ))}
              </div>
              {/* WHY, in the room's own numbers: "106 complaints and no ideas
                  yet". The choice is still the person's. */}
              {roomAdvice?.reason && (
                <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-300" data-testid="aim-recommended-why">
                  {DEPTH_WORDS[roomAdvice.depth].label} is recommended: {roomAdvice.reason}.
                </p>
              )}
            </div>
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">Where</div>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Where">
                {scopes.map(s => (
                  <Choice
                    key={s.key}
                    on={aim.scope === s.key}
                    onPick={() => onAim({ ...aim, scope: s.key })}
                    label={s.label}
                    hint={s.what}
                    testId={`aim-scope-${s.key}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {!asksOnly && (
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!ready ? (
            // IT IS WRITING THE PLAN, NOT RESEARCHING. This said "Researching
            // it", which read as the room already being off doing the work —
            // and once the button beside it was renamed Research, the same word
            // meant two different things in one window. What is happening is
            // one model call that reads the board and drafts each seat's job;
            // no dig has started and nothing has been searched.
            <div className="flex flex-col items-center gap-2 py-10 text-sm text-gray-600 dark:text-gray-300" role="status" data-testid="dig-modal-planning">
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" aria-hidden />
                Working out what to ask - reading what is already on the board…
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Nothing has been dug yet. You see the plan before it runs.
              </span>
            </div>
          ) : dig.plan ? (
            // A DIG THAT NEEDED NO BRIEF. The holes already said what is short
            // and who fills it, so there is nothing to decide here — one line
            // of what it will do, and the exact wording for whoever wants it.
            // Five prompt boxes are a debugging view, not a thing to approve.
            <div className="flex flex-col gap-2" data-testid="dig-modal-plan">
              <p className="text-sm text-gray-700 dark:text-gray-200">{dig.plan}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Nothing to write first - this starts straight away.
              </p>
              <details className="mt-1">
                <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  {dig.directive.trim() ? 'Show the exact instruction' : 'Add something to steer it'}
                </summary>
                <textarea
                  aria-label="What this dig asks"
                  value={dig.directive}
                  onChange={e => onChange({ ...dig, directive: e.target.value })}
                  rows={8}
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                />
              </details>
            </div>
          ) : (
            <div className="flex flex-col gap-2" data-testid="dig-modal-template">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No brief could be written for this, so it goes out as one instruction to{' '}
                <strong className="font-medium">{whoLabel(dig.archetype)}</strong>.
              </p>
              <textarea
                aria-label="What this dig asks"
                value={dig.directive}
                onChange={e => onChange({ ...dig, directive: e.target.value })}
                rows={8}
                className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          )}
        </div>
        )}

        <footer className="shrink-0 flex items-center gap-3 border-t border-gray-200 dark:border-gray-700 px-5 py-3">
          {budgetPicker ?? <span className="text-xs text-gray-400">{budgetLabel}</span>}
          <button
            type="button"
            onClick={onCancel}
            className="ml-auto px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            data-testid="dig-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => (asksOnly ? onResearch?.() : dig && onDig(dig))}
            disabled={!canDig}
            title={
              digging ? 'A dig is already going' : !ready && !asksOnly ? 'Still writing the brief' : undefined
            }
            className="px-4 py-1.5 rounded-md bg-teal-600 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            data-testid="dig-modal-dig"
          >
            {digging ? 'A dig is going' : asking ? '⛏ Research' : '⛏ Dig'}
          </button>
        </footer>
      </div>
    </div>
  );
}
